import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { lastValueFrom } from '../../helpers/functions.helpers';
import { getValueProperty } from '../../helpers/functions.helpers';
import { LinkupService } from '../link-up/linkup.service';

export interface ISecurity {
  client_id: string,
  client_secret: string,
  grant_type: string
}

export interface ISSOResponse {
  "access_token": string,
  "expires_in": number,
  "refresh_expires_in": number,
  "refresh_token": string,
  "token_type": string,
  "not-before-policy": number,
  "session_state": string,
  "scope": string
}

export interface IAccessToken {
  accessToken: string,
  tokenType: string
}

export interface IUserData{
  document_number: string,
  names: string
}

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  private _accessToken?: IAccessToken;
  private _clientId?: string;
  private _clientSecret?: string;
  private _grantType?: string;
  private _transactionId?: string;
  private _sessionId?: string;
  private _userData?: IUserData;

  constructor(
    private readonly _http: HttpClient,
    private readonly _link: LinkupService
  ) { }

  set clientId(value: string){
    this._clientId = value;
  }

  set clientSecret(value: string){
    this._clientSecret = value;
  }

  /**
   *
   */
  get accessToken(): Promise<IAccessToken> {
    return new Promise(async (resolve, reject) => {
      if (this._accessToken && await this.isValid()) {
        resolve(this._accessToken);
      } else {
        const accessToken = await this.loadAccessToken();
        if (accessToken) {
          const value = {
            tokenType: accessToken.token_type,
            accessToken: accessToken.access_token
          };
          this.accessToken = value;
          resolve(await this.accessToken);
        }else{
          resolve({
            tokenType: 'Bearer',
            accessToken: 'no-resolve'
          });
        }
      }
    })
  }

  /**
   * Permite establecer el valor del token
   */
  set accessToken(value: any) {
    this._accessToken = value;
  }

  /**
   *
   * @returns
   */
  async isValid() {
    const token = getValueProperty(this._accessToken, 'accessToken', false);
    if(token){
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      const { exp } = JSON.parse(jsonPayload);
      const expired = Date.now() >= ( exp - 120 ) * 1000;
      return !expired;
    }else{
      return false;
    }
  }

  /**
   * Permite cargar el token de acceso
   * @returns
   */
  async loadAccessToken() {
    let responseParent = getValueProperty(window.parent, 'webViewData', false);
    if(responseParent && responseParent.hasOwnProperty('access_token')){
      return responseParent as ISSOResponse;
    }else{
      if (!this._clientId || !this._clientSecret) {
        const applicationParameters = await this._link.applicationParameters;
        if (applicationParameters && applicationParameters.hasOwnProperty('data') && applicationParameters.data.hasOwnProperty('parameters') && applicationParameters.data.parameters.hasOwnProperty('security_sso')) {
          const security: ISecurity = applicationParameters.data.parameters.security_sso;
          this._clientId = security.client_id;
          this._clientSecret = security.client_secret;
          this._grantType = security.grant_type;
        }
      }
  
      const contextUrls = await this._link.contextUrls;
      const URL = `${contextUrls.URLS.SSO_SECURITY_TOKEN}`;
      const httpOptions: any = this.httpOptions;
  
      let body = new URLSearchParams();
  
      body.set('client_id', getValueProperty(this, '_clientId', ''));
      body.set('grant_type', getValueProperty(this, '_grantType', ''));
      body.set('client_secret', getValueProperty(this, '_clientSecret', ''));
  
      const req$ = this._http.post<ISSOResponse>(URL, body.toString(), httpOptions);
      const response: any = await lastValueFrom(req$);
      return response as ISSOResponse;
    }
  }

  /**
 * Permite construir de manera dinamica los encabezados para las peticiones al servicio de seguridad
 */
  get httpOptions() {
    return {
      headers: new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
    };
  }

  /* get transaction_id */
  get transactionId(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      if (this._transactionId) {
        resolve(this._transactionId);
      } else {
        const transactionId = await this.loadTransactionId();
        if(transactionId) {
          resolve(transactionId);
        }
      }
    });    
  }

  /* load transaction_id in sessionStorage */
  async loadTransactionId() {
    let transactionId = UUID.UUID();
    this.transactionId = transactionId;
    await sessionStorage.setItem('transaction_id', transactionId) 
    return transactionId;
  }

   /**
   * Permite establecer el valor del transactionId
   */
    set transactionId(value: any) {
      this._transactionId = value;
    }


  /* get session_id */
  get sessionId(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      if (this._sessionId) {
        resolve(this._sessionId);
      } else {
        const sessionId = await this.loadSessionId();
        if(sessionId) {
          resolve(sessionId);
        }
      }
    });    
  }

  /* load session_id in sessionStorage */
  async loadSessionId() {
    let sessionId = UUID.UUID();
    this.sessionId = sessionId;
    await sessionStorage.setItem('session_id', sessionId) 
    return sessionId;
  }

   /**
   * Permite establecer el valor del sessionId
   */
    set sessionId(value: any) {
      this._sessionId = value;
    }

  /**
   * Permite obtener los datos del usuario para la sesion actual
   */
  public get userData(): IUserData {
    if(!this._userData){
      let responseParent = getValueProperty(window.parent, 'webViewData.userData', false);
      this._userData = responseParent as IUserData;
    }
    return this._userData;
  }

  /**
   * Permite establecer los datos del usuario
   */
  public set userData(value: IUserData) {
    this._userData = value;
  }
  
}
